import { useCallback, useRef, useState } from "react";
import { AudioCallbacks, AudioPlayerContextValue } from "@checkout-core/contexts/AudioPlayerContext";
import { AUDIO_ERROR_TEXTS } from "@checkout-core/constants/AudioPlayer";

export const useAudioPlayer = (): AudioPlayerContextValue => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const play = useCallback((src: string, callbacks: AudioCallbacks = {}) => {
        if (audioRef.current) {
            audioRef.current.pause();
        }

        const newAudio = new Audio(src);
        audioRef.current = newAudio;

        newAudio.onplay = () => {
            setIsPlaying(true);
            if (typeof callbacks.onPlay === "function") {
                callbacks.onPlay();
            }
        };

        newAudio.onpause = () => {
            setIsPlaying(false);
            if (typeof callbacks.onPause === "function") {
                callbacks.onPause();
            }
        };

        newAudio.onended = () => {
            setIsPlaying(false);
            if (typeof callbacks.onEnd === "function") {
                callbacks.onEnd();
            }
        };

        newAudio.onerror = event => {
            let errorMsg = "";
            if (typeof event === "string") {
                errorMsg = event;
            } else {
                errorMsg = event.type;
            }

            setIsPlaying(false);

            if (typeof callbacks.onError === "function") {
                callbacks.onError(new Error(`${errorMsg}${AUDIO_ERROR_TEXTS.SOMETHING_WENT_WRONG}`));
            }
        };

        newAudio.play().catch(error => {
            setIsPlaying(false);
            if (typeof callbacks.onError === "function") {
                callbacks.onError(new Error(`${AUDIO_ERROR_TEXTS.PLAYBACK_FAILED} :: ${error}`));
            }
        });
    }, []);

    const pause = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
    }, []);

    const resume = useCallback((onError?: (error: Error) => void) => {
        if (audioRef.current && audioRef.current.paused) {
            audioRef.current.play().catch(error => {
                if (typeof onError === "function") {
                    onError(new Error(`${AUDIO_ERROR_TEXTS.FAILED_TO_RESUME_AUDIO} :: ${error}`));
                }
            });
        }
    }, []);

    const stop = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        }
    }, []);

    return {
        play,
        pause,
        resume,
        stop,
        isPlaying,
    };
};
